import {
  requestPostExtendTransaction,
  requestPostExtendTransactionWithSignatory,
} from "@/state/transactions/TransactionEffects";
import { Signatory, Signature } from "@/type/core/dashboard/dashboard.types";
import { CardDimension } from "@/type/core/signatory/signatory.types";
import {
  InvitationMode,
  TransactionSignatory,
} from "@/type/core/transaction/transaction.types";
import {
  SIGNATORY_STATUS_ENUM,
  U2_CARD,
  UNIVERSIGN_RATIO,
} from "@utils/Constant";
import DateFormat from "@utils/DateFormat";
import { toastError, toastSuccess } from "@utils/helpers/toast-helper";
import parse from "html-react-parser";
import { t } from "i18next";

export interface HandleRelaunchParams {
  signatureDegradedMode: boolean;
  signatoryEmail: string;
  signatoryId?: string;
  transactionId: string;
  transactionInvitationMode?: InvitationMode;
  setIsLoading: (isLoading: boolean) => void;
  callbackOnSuccess: () => void;
}

const dateToDashboardMessage = (date: string, days: number = 0) => {
  return DateFormat.dashboardDateWithDays(new Date(date), days);
};

const getCurrentSignatoryDate = (signatory: Signatory) => {
  const currentSignatory =
    signatory.status === "waiting" ||
    signatory.status === "inProgress" ||
    signatory.status === "error";
  let signatoryDate = null;
  if (signatory.invitationDate && !signatory.relaunchDate)
    signatoryDate =
      "(Invité - " + dateToDashboardMessage(signatory.invitationDate, 0) + ")";
  if (!signatory.invitationDate && signatory.relaunchDate)
    signatoryDate =
      "(Relancé - " + dateToDashboardMessage(signatory.relaunchDate, 0) + ")";
  if (signatory.invitationDate && signatory.relaunchDate)
    signatoryDate =
      "(Relancé - " + dateToDashboardMessage(signatory.relaunchDate, 0) + ")";
  return currentSignatory && signatoryDate;
};

export const setSignatoryColor = (
  signatory: Signatory,
  status: string,
): string | JSX.Element | JSX.Element[] | undefined => {
  let signatoryStatusColor = "";
  for (const key in SIGNATORY_STATUS_ENUM) {
    const signatoryStatusEnum = SIGNATORY_STATUS_ENUM[key];
    if (signatoryStatusEnum[0] === signatory.status) {
      signatoryStatusColor = signatoryStatusEnum[1];
    }
  }
  const signatoryDate = getCurrentSignatoryDate(signatory);
  if (status !== "expired") {
    return parse(
      `<span class=${signatoryStatusColor}>${signatory.firstname} ${
        signatory.lastname
      }</span>
          ${
            signatoryDate
              ? `<div class=${signatoryStatusColor}><i>${signatoryDate}</i></div>`
              : ""
          }`,
    );
  } else if (status === "expired") {
    return parse(
      `<span class=${signatoryStatusColor}>${signatory.firstname} ${signatory.lastname}</span>
          <div class=${signatoryStatusColor}></div>`,
    );
  }
  return undefined;
};

export const getCardDimension = (pdf: HTMLCanvasElement): CardDimension => {
  const bounds = pdf.getBoundingClientRect();
  const windowDevicePixelRatio = window.devicePixelRatio;
  const UNIVERSIGN_WIDTH =
    pdf.width / windowDevicePixelRatio / UNIVERSIGN_RATIO;
  const UNIVERSIGN_HEIGHT =
    pdf.height / windowDevicePixelRatio / UNIVERSIGN_RATIO;
  const ratioWidth = U2_CARD.cardWidth / UNIVERSIGN_WIDTH;
  const ratioHeight = U2_CARD.cardHeight / UNIVERSIGN_HEIGHT;
  const width = Math.round(ratioWidth * bounds.width);
  const height = Math.round(ratioHeight * bounds.height);

  return {
    width,
    height,
  };
};

export const createTransactionSignatories = (
  signatories: Signatory[],
): TransactionSignatory[] => {
  return signatories.map((signatory) => {
    return {
      firstname: signatory.firstname,
      lastname: signatory.lastname,
      email: signatory.email,
      phone: signatory.phone,
      certificate: signatory.certificate,
      level: signatory.level,
      withCertificate: signatory.withCertificate ?? null,
      pattern: signatory.pattern,
    };
  });
};

export const sortSignatories = (a: Signatory, b: Signatory): number => {
  if (!a.rank || !b.rank) {
    return 0;
  }
  if (a.rank > b.rank) {
    return 1;
  }
  if (a.rank < b.rank) {
    return -1;
  }
  return 0;
};

export const allSignatoriesHaveAtLeastOneSignature = (
  signatories: Signatory[],
  signatures: Signature[],
): boolean => {
  const signatureEmails = new Set(signatures.map((s) => s.email));
  return signatories.every((signatory) => signatureEmails.has(signatory.email));
};

export const handleRelaunchSignatory = async ({
  signatureDegradedMode,
  signatoryEmail,
  signatoryId,
  transactionId,
  transactionInvitationMode,
  setIsLoading,
  callbackOnSuccess,
}: HandleRelaunchParams): Promise<void> => {
  if (signatureDegradedMode) {
    return;
  }

  setIsLoading(true);

  if (transactionInvitationMode === "parallel") {
    if (!signatoryId) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await requestPostExtendTransactionWithSignatory({
        id: signatoryId,
        midId: transactionId,
      });

      if (response.ok) {
        toastSuccess(
          t("success.transactionDetail.signatoryRelaunch", {
            email: signatoryEmail,
          }),
        );
        callbackOnSuccess?.();
      } else {
        toastError(t("error.transactionDetail.signatoryRelaunch"));
      }
    } catch {
      toastError(t("error.transactionDetail.signatoryRelaunch"));
    } finally {
      setIsLoading(false);
    }
  } else {
    try {
      const response = await requestPostExtendTransaction({
        id: transactionId,
      });

      if (response.ok) {
        toastSuccess(
          t("success.transactionDetail.signatoryRelaunch", {
            email: signatoryEmail,
          }),
        );
        callbackOnSuccess?.();
      } else {
        toastError(t("error.transactionDetail.signatoryRelaunch"));
      }
    } catch {
      toastError(t("error.transactionDetail.signatoryRelaunch"));
    } finally {
      setIsLoading(false);
    }
  }
};
